import React, { useState, useEffect } from "react";
import { Table, Button, Input, Tag, Tooltip } from "antd";
import { CloseCircleOutlined, SearchOutlined, FolderOutlined, FolderOpenOutlined } from "@ant-design/icons";
import { ToastContainer, toast, Bounce } from "react-toastify";
import config from "../../config";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import PermissionGuard from "../Guards/PermissionGuard";
import { PERMISSIONS } from "../../constants/permissions";
import ExportExcelButton from "../Commons/ExportExcelButton";
import ExamBankActions from "./ExamBank/ExamBankActions";
import ExamBankChildren from "./ExamBank/ExamBankChildren";
import { sortByOrder, getItemOrder } from "../../utils/examBankOrder";

const buildExamBanksFilter = (filtersFromCaller) => {
    const filters = [];
    if (filtersFromCaller.title) filters.push(`title=*${filtersFromCaller.title}/i`);
    return filters.join(",");
};

const normalizeExamBankItem = (item) => {
    const exams = sortByOrder(
        (item.exams ?? item.Exams ?? []).map((exam, index) => ({
            ...(typeof exam === "object" ? exam : { id: exam }),
            order: getItemOrder(exam, index),
        }))
    );
    const childBanks = sortByOrder(
        (item.childBanks ?? item.ChildBanks ?? []).map((bank, index) => ({
            ...(typeof bank === "object" ? bank : { id: bank }),
            order: getItemOrder(bank, index),
        }))
    );
    const users = item.users ?? item.Users ?? [];

    const examCount = exams.length || item.examCount || item.ExamCount || 0;
    const userCount = users.length || item.userCount || item.UserCount || 0;
    const childCount = childBanks.length || item.childCount || item.ChildCount || 0;
    const hasChildren = childBanks.length > 0 || item.hasChildren || item.HasChildren || false;
    const order = item.order ?? item.Order ?? 0;

    return {
        Id: item.id ?? item.Id ?? item.examBankId,
        Title: item.title ?? item.Title ?? "",
        QrCode: item.qrCode ?? item.QrCode ?? "",
        Version: item.version ?? item.Version ?? 0,
        Order: order,
        ExamCount: examCount,
        UserCount: userCount,
        ChildCount: childCount,
        HasChildren: hasChildren,
        Exams: exams,
        Users: users,
        ChildBanks: childBanks,
    };
};

const extractListData = (payload) => {
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.data)) return payload.data;
    return [];
};

const ExamBankGrid = () => {
    const [search, setSearch] = useState("");
    const [exams, setExams] = useState([]);
    const navigate = useNavigate();
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
    });
    const [searchFilters, setSearchFilters] = useState({
        title: "",
    });

    // Hierarchical state
    const [expandedNodes, setExpandedNodes] = useState({}); // { [bankId]: true/false }
    const [loadedChildren, setLoadedChildren] = useState({}); // { [bankId]: [children] }
    const [loadingChildren, setLoadingChildren] = useState({}); // { [bankId]: true/false }

    const getBankId = (record) => record?.Id ?? record?.id;

    const handleSearchChange = (e, field) => {
        const value = e?.target ? e.target.value : e;
        setSearchFilters((prev) => ({ ...prev, [field]: value }));
    };

    const fetchExamsWithFilters = (override = {}) => {
        const effectiveFilters = { ...searchFilters, ...override };
        fetchExams(1, pagination.pageSize, effectiveFilters);
    };

    const fetchExams = async (page = 1, pageSize = 10, filtersFromCaller = searchFilters) => {
        setLoading(true);
        try {
            const requestBody = {
                Page: page,
                PageSize: pageSize,
                Filter: buildExamBanksFilter(filtersFromCaller),
                OrderBy: "",
            };
            const response = await axios.post(
                `${config.apiBaseUrl}/api/ExamBank/GetList`,
                requestBody,
                {
                    headers: {
                        "Content-Type": "application/json",
                        HeaderKey: config.HeaderKey,
                    },
                    withCredentials: true,
                }
            );
            const data = extractListData(response.data.data);
            const total = response.data.data?.count ?? data.length;

            const transformedItems = data.map(normalizeExamBankItem);
            
            setExams(transformedItems);
            setPagination((prev) => ({ ...prev, total, current: page, pageSize }));
        } catch (err) {
            toast.error(<p dir="rtl" className="font-custom">جستجو با خطا مواجه شد</p>);
            if (err.response?.status === 401) {
                navigate("/");
            }
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = async (record) => {
        const id = record.Id || record.id;
        const title = record.Title || record.title;

        const navigateToForm = (bankData) => {
            navigate(`/dashboard/exambanks/form`, {
                state: { initialBank: bankData },
            });
        };

        try {
            const response = await axios.get(
                `${config.apiBaseUrl}/api/ExamBank/GetExamBank?id=${id}`,
                {
                    headers: {
                        "Content-Type": "application/json",
                        HeaderKey: config.HeaderKey,
                    },
                    withCredentials: true,
                }
            );

            const bank = response.data?.data ?? response.data;
            if (bank) {
                navigateToForm({
                    id: bank.id ?? bank.Id ?? id,
                    title: bank.title ?? bank.Title ?? title,
                    qrCode: bank.qrCode ?? bank.QrCode ?? record.QrCode ?? "",
                    version: bank.version ?? bank.Version ?? record.Version ?? record.version ?? 0,
                    exams: bank.subExams ?? bank.exams ?? bank.Exams ?? record.Exams ?? [],
                    childBanks: bank.childExamBanks ?? bank.childBanks ?? bank.ChildBanks ?? record.ChildBanks ?? [],
                    users: bank.users ?? bank.Users ?? record.Users ?? [],
                    userIds: bank.userIds ?? bank.UserIds ?? bank.userIDs ?? record.userIds ?? [],
                });
                return;
            }
        } catch {
            // Fall back to list data below
        }

        const exams = record.Exams || record.exams || [];
        const childBanks = record.ChildBanks || record.childBanks || [];
        const users = record.Users || record.users || [];

        if (exams.length > 0 || childBanks.length > 0 || users.length > 0) {
            navigateToForm({
                id,
                title,
                qrCode: record.QrCode ?? record.qrCode ?? "",
                version: record.Version ?? record.version ?? 0,
                exams,
                childBanks,
                users,
                userIds: record.userIds ?? record.UserIds ?? [],
            });
            return;
        }

        try {
            const response = await axios.post(
                `${config.apiBaseUrl}/api/ExamBank/GetList`,
                {
                    Page: 1,
                    PageSize: 1,
                    Filter: `id=${id}`,
                    OrderBy: "",
                },
                {
                    headers: {
                        "Content-Type": "application/json",
                        HeaderKey: config.HeaderKey,
                    },
                    withCredentials: true,
                }
            );
            const data = extractListData(response.data.data);
            const fullRecord = data.length > 0 ? normalizeExamBankItem(data[0]) : null;

            navigateToForm({
                id,
                title,
                qrCode: fullRecord?.QrCode || "",
                version: fullRecord?.Version ?? 0,
                exams: fullRecord?.Exams || [],
                childBanks: fullRecord?.ChildBanks || [],
                users: fullRecord?.Users || [],
                userIds: fullRecord?.userIds || [],
            });
        } catch (err) {
            console.error("Failed to fetch bank details:", err);
            navigateToForm({
                id,
                title,
                qrCode: record.QrCode ?? record.qrCode ?? "",
                version: record.Version ?? record.version ?? 0,
                exams: [],
                childBanks: [],
                users: [],
                userIds: [],
            });
        }
    };

    const handleDelete = (id) => {
        toast.warn(
            <div className="text-right font-custom">
                <p>آیا از حذف این بانک آزمون مطمئن هستید؟</p>
                <div className="flex justify-end gap-2 mt-2">
                    <button
                        className="bg-red-500 hover:bg-red-700 text-white px-3 py-1 rounded"
                        onClick={() => confirmDelete(id)}
                    >
                        حذف
                    </button>
                    <button
                        className="bg-gray-500 hover:bg-gray-700 text-white px-3 py-1 rounded"
                        onClick={() => toast.dismiss()}
                    >
                        انصراف
                    </button>
                </div>
            </div>,
            { autoClose: false, icon: false }
        );
    };

    const confirmDelete = async (id) => {
        try {
            const response = await axios.delete(`${config.apiBaseUrl}/api/ExamBank/DeleteExamBank?id=${id}`, {
                headers: {
                    "Content-Type": "application/json",
                    "HeaderKey": config.HeaderKey
                },
                withCredentials: true,
            });
            
            if (!response.status || response.status !== 200) {
                toast.error(<p dir="rtl" className="font-custom">حذف با خطا مواجه شد</p>);
            }
            else {
                toast.success(<p dir="rtl" className="font-custom">حذف با موفقیت انجام شد</p>);
            }
            fetchExams(pagination.current, pagination.pageSize);
        } catch (err) {
            console.error("Delete error:", err);
            toast.error(<p dir="rtl" className="font-custom">حذف با خطا مواجه شد</p>);
        }
    };

    const handleExpand = async (record) => {
        const bankId = getBankId(record);
        if (!bankId) {
            console.error("Bank ID is undefined:", record);
            return;
        }

        const isCurrentlyExpanded = expandedNodes[bankId];

        setExpandedNodes((prev) => ({ ...prev, [bankId]: !prev[bankId] }));

        if (isCurrentlyExpanded || loadedChildren[bankId] || loadingChildren[bankId] || !record.HasChildren) {
            return;
        }

        setLoadingChildren((prev) => ({ ...prev, [bankId]: true }));
        try {
            const response = await axios.get(
                `${config.apiBaseUrl}/api/ExamBank/GetChildren?id=${bankId}`,
                {
                    headers: {
                        "Content-Type": "application/json",
                        HeaderKey: config.HeaderKey,
                    },
                    withCredentials: true,
                }
            );

            if (response.data.success) {
                const children = sortByOrder(
                    extractListData(response.data.data).map(normalizeExamBankItem)
                );
                setLoadedChildren((prev) => ({ ...prev, [bankId]: children }));
            } else {
                toast.error(<p dir="rtl" className="font-custom">خطا در دریافت فرزندان</p>);
            }
        } catch (err) {
            console.error("Failed to fetch children:", err);
            toast.error(<p dir="rtl" className="font-custom">خطا در دریافت فرزندان</p>);
        } finally {
            setLoadingChildren((prev) => ({ ...prev, [bankId]: false }));
        }
    };

    const handleManageExams = (record) => {
        navigate(`/dashboard/exams`, { state: { examBank: record } });
    };

    const handleAddChild = (record) => {
        const parentId = record.Id || record.id;
        navigate(`/dashboard/exambanks/form`, {
            state: {
                parentBank: {
                    id: parentId,
                    title: record.Title || record.title,
                },
            },
        });
    };

    const ClearSuffix = ({ onClick, style }) => (
        <Tooltip title="پاککردن">
            <CloseCircleOutlined
                onClick={onClick}
                style={{ cursor: "pointer", ...style, textAlign: 'left' }}
                role="button"
                aria-label="clear"
                tabIndex={0}
                onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onClick()}
            />
        </Tooltip>
    );

    useEffect(() => {
        fetchExams(1, 10);
    }, []);

    const columns = [
        {
            title: "عملیات",
            key: "actions",
            fixed: "left",
            width: 120,
            className: "font-custom",
            align: "center",
            render: (_, record) => (
                <div style={{
                    direction: "rtl",
                }}>
                    <ExamBankActions
                        record={record}
                        onEdit={handleEdit}
                        onDelete={handleDelete}
                        onAddChild={handleAddChild}
                        onManageExams={handleManageExams}
                        showAddChild={true}
                        showManageExams={true}
                    />

                </div>
            ),
        },
        {
            title: "تعداد آزمونها",
            dataIndex: "ExamCount",
            className: "font-custom",
            align: "center",
            key: "exams",
            width: 120,
            render: (count) => <Tag>{count}</Tag>,
        },
        {
            title: "تعداد کاربران",
            dataIndex: "UserCount",
            className: "font-custom",
            align: "center",
            key: "users",
            width: 110,
            render: (count) => <Tag>{count}</Tag>,
        },
        {
            title: "تعداد فرزندان",
            dataIndex: "ChildCount",
            className: "font-custom",
            align: "center",
            key: "children",
            width: 120,
            render: (count) => <Tag>{count}</Tag>,
        },
        {
            title: "عنوان",
            className: "font-custom",
            align: "right",
            dataIndex: "Title",
            key: "title",
            filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
                <div style={{ padding: 8 }}>
                    <Input
                        placeholder="جستجوی عنوان"
                        value={searchFilters.title}
                        onChange={(e) => handleSearchChange(e, "title")}
                        onPressEnter={() => fetchExamsWithFilters()}
                        suffix={
                            <ClearSuffix
                                onClick={() => {
                                    fetchExamsWithFilters({ title: "" });
                                    setSearchFilters((prev) => ({ ...prev, title: "" }));
                                }}
                                style={{ visibility: searchFilters.title ? "visible" : "hidden" }}
                            />
                        }
                        style={{ marginBottom: 8, display: "flex", direction: "rtl" }}
                    />
                    <Button
                        type="primary"
                        onClick={() => fetchExamsWithFilters()}
                        icon={<SearchOutlined size={16} />}
                        size="small"
                        className="font-custom"
                    >
                        جستجو
                    </Button>
                </div>
            ),
            render: (text, record) => {
                const bankId = getBankId(record);
                const isExpanded = expandedNodes[bankId];
                const isLoading = loadingChildren[bankId];

                return (
                    <div  dir="rtl" style={{ textAlign: "right" }} className="flex items-center gap-2">
                        {isLoading || isExpanded ? (
                            <FolderOpenOutlined className="text-blue-500" />
                        ) : (
                            <Tooltip title="باز کردن">
                                <FolderOutlined className="text-gray-500" />
                            </Tooltip>
                        )}
                        <span>{text}</span>
                    </div>
                );
            },
        }
    ];

    const expandedRowRender = (record) => {
        const bankId = getBankId(record);

        return (
            <ExamBankChildren
                children={loadedChildren[bankId] || []}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onAddChild={handleAddChild}
                onManageExams={handleManageExams}
                onExpand={handleExpand}
                level={1}
                isLoading={loadingChildren[bankId]}
                loadedChildren={loadedChildren}
                loadingChildren={loadingChildren}
                expandedNodes={expandedNodes}
            />
        );
    };

    return (
        <div className="font-custom rtl text-right p-5">
            <h1 className="text-center text-xl mt-5 mb-5">
                لیست بانکهای آزمون
            </h1>
            <div className="flex gap-2">
                <PermissionGuard permission={PERMISSIONS.EXAMBANKS_VIEW}>
                    <ExportExcelButton
                        endpoint={`${config.apiBaseUrl}/api/ExamBank/ExportExcel`}
                        filter={buildExamBanksFilter(searchFilters)}
                        fileName="exambanks.xlsx"
                    />
                </PermissionGuard>
                <PermissionGuard permission={PERMISSIONS.EXAMBANKS_CREATE}>
                    <button
                        className="bg-green-700 hover:bg-green-600 text-white font-semibold py-2 px-4 rounded-full shadow-md transition duration-300 mb-5"
                        onClick={() => navigate("form")}
                    >
                        + جدید
                    </button>
                </PermissionGuard>
            </div>
            <div className="w-full shadow-lg">
                <Table
                    columns={columns}
                    dataSource={exams}
                    loading={loading}
                    rowKey={(record) => getBankId(record)}
                    onRow={(record) => ({
                        onClick: () => handleExpand(record),
                        className: "cursor-pointer hover:bg-gray-50",
                    })}
                    expandable={{
                        showExpandColumn: false,
                        expandedRowRender,
                        rowExpandable: (record) => !!record.HasChildren,
                        expandRowByClick: false,
                        expandedRowKeys: exams
                            .map(getBankId)
                            .filter((id) => expandedNodes[id]),
                        onExpand: (expanded, record) => {
                            const bankId = getBankId(record);
                            if (expanded) {
                                handleExpand(record);
                            } else {
                                setExpandedNodes((prev) => ({ ...prev, [bankId]: false }));
                            }
                        },
                    }}
                    pagination={
                        {
                            current: pagination.current,
                            pageSize: pagination.pageSize,
                            total: pagination.total,
                            showSizeChanger: true,
                            pageSizeOptions: ['5', '10', '20', '50'],
                            onChange: (page, pageSize) => {
                                setPagination((prev) => ({
                                    ...prev,
                                    current: page,
                                    pageSize: pageSize,
                                }));
                                fetchExams(page, pageSize);
                            },
                            showTotal: (total, range) => `${range[0]}-${range[1]} از ${total}`,
                        }
                    }
                    bordered
                />
            </div>
            <ToastContainer
                position="bottom-center"
                autoClose={5000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                className={"font-custom text-center rtl"}
                pauseOnHover
                theme="dark"
                transition={Bounce}
            />
        </div>
    );
};

export default ExamBankGrid;